/**
 * ShippingEstimator Component (Synced & Optimized)
 * Features:
 * 1. Global Event Bus for cross-component sync.
 * 2. Static Fetch Promise to prevent duplicate API calls.
 * 3. Static Data Parsing to save CPU.
 * 4. LocalStorage persistence.
 */
if (!customElements.get('shipping-estimator')) {
  class ShippingEstimator extends PopupBase {
    // Static properties to share data across instances
    static fetchPromise = null; 
    static parsedData = null;

    constructor() {
      super();
      
      // DOM Elements
      this.triggerBtn = this.querySelector('.shipping-location-trigger');
      this.locationText = this.querySelector('.location-name');
      this.timeText = this.querySelector('.time-text');
      this.template = this.querySelector('#shipping-estimator-popup-template');
      
      // Configuration
      this.storageKey = 'shipping_estimator_user_preference';
      this.defaultTextPattern = this.dataset.textPattern;
      this.fallbackText = this.dataset.textFallback;
      this.popupTitleText = this.dataset.popupTitle || 'Select your location';
      this.textDetecting = this.dataset.textDetecting || 'Detecting';
      this.deliveryDatePattern = this.dataset.deliveryDatePattern; 
      this.locale = this.dataset.locale || 'en-US'; 
      this.currentState = null;

      // Initialize
      this.init();
    }

    init() {
      // 1. Listen for global updates (Sync mechanism)
      document.addEventListener('shipping:location-updated', (e) => {
        if (e.detail && e.detail.state !== this.currentState) {
          this.updateEstimation(e.detail.state);
        }
      });

      // 2. Check LocalStorage
      const cachedState = localStorage.getItem(this.storageKey);
      const data = this.getShippingData();

      if (cachedState && data && data.hasOwnProperty(cachedState)) {
        this.updateEstimation(cachedState);
      } else {
        this.detectLocation();
      }

      // 3. Bind UI Events (Only if trigger exists - Full Mode)
      if (this.triggerBtn) {
        this.triggerBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openLocationPopup();
        });
      }
    }

    /**
     * Efficiently retrieves shipping data. Parses once and caches in static property.
     */
    getShippingData() {
      if (ShippingEstimator.parsedData) return ShippingEstimator.parsedData;

      try {
        const rawData = this.dataset.shippingData;
        if (!rawData) return {};
        ShippingEstimator.parsedData = JSON.parse(rawData);
        return ShippingEstimator.parsedData;
      } catch (e) {
        console.error('Shipping Estimator: Invalid JSON.', e);
        return {};
      }
    }

    /**
     * Optimized Location Detection
     * Prevents multiple API calls if multiple components exist on the page.
     */
    detectLocation() {
      if (this.locationText) this.locationText.innerText = this.textDetecting;

      // Return existing promise if a request is already in flight
      if (ShippingEstimator.fetchPromise) {
        ShippingEstimator.fetchPromise.then(region => {
          if (region) this.handleDetectionSuccess(region);
          else this.handleUnknownLocation();
        });
        return;
      }

      // Create new fetch promise
      ShippingEstimator.fetchPromise = fetch('https://ipwho.is/')
        .then(response => response.json())
        .then(data => {
          if (data.success && data.region) return data.region;
          return null;
        })
        .catch(err => {
          console.warn('Location detection failed.', err);
          return null;
        });

      // Execute promise
      ShippingEstimator.fetchPromise.then(region => {
        if (region) this.handleDetectionSuccess(region);
        else this.handleUnknownLocation();
      });
    }

    handleDetectionSuccess(region) {
      const data = this.getShippingData();
      if (data && data.hasOwnProperty(region)) {
        // Save to storage
        localStorage.setItem(this.storageKey, region);
        // Update self
        this.updateEstimation(region);
        // Broadcast to other components (Sync)
        this.broadcastUpdate(region);
      } else {
        this.handleUnknownLocation(region);
      }
    }

    /**
     * Dispatches a custom event to notify other instances
     */
    broadcastUpdate(stateName) {
      const event = new CustomEvent('shipping:location-updated', {
        detail: { state: stateName }
      });
      document.dispatchEvent(event);
    }

    updateEstimation(stateName) {
      // Prevent redundant updates
      if (this.currentState === stateName) {
        if (this.timeText) this.timeText.classList.remove('skeleton-loading');
        if (this.locationText) this.locationText.classList.remove('skeleton-loading');
        return;
      }

      this.currentState = stateName;
      const data = this.getShippingData();
      
      // Update Location Name
      if (this.locationText) {
        this.locationText.classList.remove('skeleton-loading');
        this.locationText.innerText = stateName;
      }

      // Update Shipping Time
      if (this.timeText) {
        this.timeText.classList.remove('skeleton-loading');
        
        const rawValue = String(data[stateName]).trim(); 
        
        let minDays = 0;
        let maxDays = 0;
        let isValid = false;

        if (rawValue.includes('-')) {
          const parts = rawValue.split('-');
          minDays = parseInt(parts[0]);
          maxDays = parseInt(parts[1]);
          isValid = !isNaN(minDays) && !isNaN(maxDays);
        } else {
          minDays = parseInt(rawValue);
          maxDays = minDays; 
          isValid = !isNaN(minDays);
        }
        
        if (isValid) {
          const startDate = new Date();
          startDate.setDate(startDate.getDate() + minDays);
          const startDateStr = this.formatDate(startDate);

          let finalDateString = startDateStr;

          if (maxDays > minDays) {
            const endDate = new Date();
            endDate.setDate(endDate.getDate() + maxDays);
            const endDateStr = this.formatDate(endDate);
            finalDateString = `${startDateStr} - ${endDateStr}`;
          }

          let message = '';
          
          if (this.deliveryDatePattern && this.deliveryDatePattern.includes('[date]')) {
             message = this.deliveryDatePattern.replace('[date]', finalDateString);
          } else {
             message = this.defaultTextPattern.replace('[days]', rawValue);
          }
          
          this.timeText.innerText = message;
        } else {
          this.timeText.innerText = this.fallbackText;
        }
      }
    }

    formatDate(date) {
      if (this.locale.startsWith('en')) {
        const weekday = date.toLocaleString(this.locale, { weekday: 'long' });
        const month = date.toLocaleString(this.locale, { month: 'short' });
        const day = date.getDate();
        const suffix = this.getOrdinalSuffix(day);
        return `${weekday}, ${month} ${day}${suffix}`;
      } 
      return date.toLocaleString(this.locale, { month: 'short', day: 'numeric', weekday: 'long' });
    }

    getOrdinalSuffix(day) {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1:  return "st";
        case 2:  return "nd";
        case 3:  return "rd";
        default: return "th";
      }
    }

    handleUnknownLocation(regionName = 'Select Location') {
      if (this.locationText) {
        this.locationText.classList.remove('skeleton-loading');
        this.locationText.innerText = regionName;
      }
      if (this.timeText) {
        this.timeText.classList.remove('skeleton-loading');
        this.timeText.innerText = this.fallbackText;
      }
    }

    openLocationPopup() {
      if (!this.template) return;

      const contentFragment = this.template.content.cloneNode(true);
      const tempDiv = document.createElement('div');
      tempDiv.appendChild(contentFragment);
      
      const listContainer = tempDiv.querySelector('.shipping-state-list');
      this.buildStateList(listContainer);

      const searchInput = tempDiv.querySelector('.shipping-search-input');
      if (searchInput) {
        searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value, listContainer));
      }

      const popupHeader = `<h3 class="title-popup h5 my-0 px-20 px-md-30 py-20 border-bottom">${this.popupTitleText}</h3>`;
      
      this.initPopup(tempDiv.innerHTML, popupHeader);

      setTimeout(() => {
        this.bindPopupEvents();
      }, 50);
    }

    buildStateList(container) {
      if (!container) return;
      const data = this.getShippingData();
      const states = Object.keys(data).sort();

      const html = states.map(state => `
        <li class="shipping-state-item p-15 border-bottom cursor-pointer hover-grey-bg transition" 
            role="option" 
            tabindex="0"
            data-state="${state}"
            aria-selected="${state === this.currentState}">
          <span class="fs-14 heading-color">${state}</span>
        </li>
      `).join('');

      container.innerHTML = html;
    }

    bindPopupEvents() {
      const modalContent = document.querySelector('.tingle-modal--visible .tingle-modal-box__content') || document.body;

      const searchInput = modalContent.querySelector('.shipping-search-input');
      const listContainer = modalContent.querySelector('.shipping-state-list');
      
      if (searchInput && listContainer) {
        searchInput.focus();
        searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value, listContainer));
      }

      const items = modalContent.querySelectorAll('.shipping-state-item');
      items.forEach(item => {
        item.addEventListener('click', () => {
          const selectedState = item.dataset.state;
          
          localStorage.setItem(this.storageKey, selectedState);
          
          // Update self
          this.updateEstimation(selectedState);
          
          // Broadcast to other components (Simple Version)
          this.broadcastUpdate(selectedState);
          
          const closeBtn = document.querySelector('.tingle-modal__close');
          if (closeBtn) closeBtn.click();
        });

        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') item.click();
        });
      });
    }

    handleSearch(query, listContainer) {
      const lowerQuery = query.toLowerCase();
      const items = listContainer.querySelectorAll('.shipping-state-item');
      
      items.forEach(item => {
        const stateName = item.dataset.state.toLowerCase();
        if (stateName.includes(lowerQuery)) {
          item.style.display = 'block';
        } else {
          item.style.display = 'none';
        }
      });
    }
  }

  customElements.define('shipping-estimator', ShippingEstimator);
}